import React from 'react';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import classNames from 'classnames';
import cuid from 'cuid';
import PropTypes from 'prop-types';
import ConstituentCategories from '@/constants/constituents/ConstituentCategories';
import LegacyConstituentRules from '@/constants/constituents/LegacyConstituentRules';
import ConstituentIcons from '@/lib/configs/ConstituentIcons';
import translate from '@/lib/intl/translate';

// TODO(jon): We're going to be adding "uncategorized" as an option, but we can
//   address that in a future PR once the BE supports that search param and we
//   have designs for what the selected state of "uncategorized" looks like.
// const options = [...ConstituentIcons, { value: 'uncategorized', label: translate('CONSTITUENT_UNCATEGORIZED') }];

function ConstituentCategorySelect({
  className,
  hideDonorRule,
  id,
  label,
  multiple,
  onChange,
  value,
  wrapValues,
  v2,
}) {
  const [uid] = React.useState(id || cuid());

  const internalValue = React.useMemo(
    () =>
      ConstituentIcons.filter((item) =>
        value?.includes(v2 ? item.valueV2 : item.value),
      ),
    [value],
  );

  const handleChange = (event) => {
    if (multiple) {
      onChange(
        event.target.value.map((item) => (v2 ? item.valueV2 : item.value)),
      );
    } else {
      const val = v2 ? event.target.value.valueV2 : event.target.value.value;
      onChange([val]);
    }
  };

  const donorCategory = v2
    ? ConstituentCategories.CATEGORY_DONOR
    : LegacyConstituentRules.IS_DONOR_RULESET;

  const icons = hideDonorRule
    ? ConstituentIcons.filter((cnx) => cnx.value !== donorCategory)
    : ConstituentIcons;

  return (
    <FormControl className={classNames(className, 'w-full')} size="small">
      <InputLabel id={`constituent-category-select-label-${uid}`}>
        {label}
      </InputLabel>
      <Select
        labelId={`constituent-category-select-label-${uid}`}
        id={`constituent-category-select-${uid}`}
        label={label}
        multiple={multiple}
        value={internalValue}
        onChange={handleChange}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }}
        renderValue={(selected) => (
          <div
            className={classNames({
              'max-w-full truncate': !wrapValues,
              'flex flex-wrap': wrapValues,
            })}
          >
            {selected.map((item, i) => (
              <div
                key={v2 ? item.valueV2 : item.value}
                className="mr-3 inline-flex items-center"
              >
                {item.icon && <span className="mr-1">{item.icon}</span>}
                {item.label + (i < selected.length - 1 ? ',' : '')}
              </div>
            ))}
          </div>
        )}
      >
        {icons.map((item) => (
          <MenuItem key={v2 ? item.valueV2 : item.value} value={item}>
            <div className="flex w-full items-center">
              {item.icon}
              <div className="mx-3">{item.label}</div>
              {multiple && (
                <Checkbox
                  className="ml-auto"
                  checked={internalValue.includes(item)}
                />
              )}
            </div>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

ConstituentCategorySelect.propTypes = {
  className: PropTypes.string,
  hideDonorRule: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.string,
  multiple: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.array,
  wrapValues: PropTypes.bool,
  v2: PropTypes.bool,
};

ConstituentCategorySelect.defaultProps = {
  className: '',
  hideDonorRule: false,
  id: null,
  label: translate('CONSTITUENT_CATEGORY'),
  multiple: true,
  onChange: () => {},
  value: [],
  wrapValues: false,
  v2: false,
};

export default ConstituentCategorySelect;
